import React from 'react';

import { useIntl } from 'react-intl-next';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import {
	TOOLBAR_RANK,
	AI_SECTION_RANK,
	ROVO_GROUP_RANK,
	ROVO_MENU_RANK,
	IMPROVE_WRITING_GROUP_RANK,
	TOOLBARS,
	AI_SECTION,
	ROVO_GROUP,
	ROVO_MENU,
	ROVO_PROMPT_MENU_SECTION_RANK,
	CHANGE_TONE_MENU_RANK,
	CHANGE_TONE_MENU_SECTION_RANK,
	TRANSLATE_MENU_RANK,
	IMPROVE_WRITING_GROUP,
	ROVO_HERO_BUTTON,
	ROVO_PROMPT_MENU_SECTION,
	MAKE_SHORTER_MENU_ITEM,
	MAKE_LONGER_MENU_ITEM,
	CHANGE_TONE_NESTED_MENU,
	CHANGE_TONE_MENU_SECTION,
	MORE_PROFESSIONAL_MENU_ITEM,
	MORE_CASUAL_MENU_ITEM,
	MORE_EMPATHETIC_MENU_ITEM,
	SPELLING_AND_GRAMMAR_MENU_ITEM,
	TRANSLATE_NESTED_MENU,
	TRANSLATE_MENU_SECTION,
	IMPROVE_WRITING_BUTTON,
	SUMMARIZE_MENU_ITEM,
} from '@atlaskit/editor-common/toolbar';
import type { ToolbarButtonGroupLocation } from '@atlaskit/editor-toolbar';
import {
	MoreItemsIcon,
	AIBriefcaseIcon,
	AICasualIcon,
	AICommandIcon,
	AIChatIcon,
	AIHeartIcon,
	AIShortenIcon,
	AISpellcheckIcon,
	AIChangeToneIcon,
	AILengthenIcon,
	ToolbarButton,
	ToolbarDropdownMenu,
	ToolbarNestedDropdownMenu,
	ToolbarTooltip,
	ToolbarDropdownItem,
	NestedDropdownRightIcon,
	AISummarizeIcon,
	AITranslateIcon,
} from '@atlaskit/editor-toolbar';
import type { RegisterComponent } from '@atlaskit/editor-toolbar-model';

const RovoButton = ({ groupLocation }: { groupLocation?: ToolbarButtonGroupLocation }) => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarTooltip content={formatMessage(aiMessages.askRovoToolbarIconTitle)}>
			<ToolbarButton
				iconBefore={<AIChatIcon label="" />}
				groupLocation={groupLocation}
				testId="editor-toolbar__ask-rovo"
			>
				{formatMessage(aiMessages.askRovoToolbarIconTitle)}
			</ToolbarButton>
		</ToolbarTooltip>
	);
};

const RovoMenu = ({
	children,
	groupLocation,
}: {
	children: React.ReactNode;
	groupLocation?: ToolbarButtonGroupLocation;
}) => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarDropdownMenu
			iconBefore={<MoreItemsIcon label={formatMessage(aiMessages.moreRovoOptionsMenuLabel)} />}
			groupLocation={groupLocation}
			testId="editor-toolbar__ask-rovo-menu"
		>
			{children}
		</ToolbarDropdownMenu>
	);
};

const MakeShorterButton = () => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarDropdownItem
			elemBefore={<AIShortenIcon label="" />}
			testId="dropdown-item__make-shorter"
		>
			{formatMessage(aiMessages.makeShorterToolbarDropdownIconTitle)}
		</ToolbarDropdownItem>
	);
};

const ChangeToneMenu = ({ children }: { children: React.ReactNode }) => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarNestedDropdownMenu
			elemBefore={<AIChangeToneIcon label="" />}
			text={formatMessage(aiMessages.changeToneToolbarDropdownIconTitle)}
			elemAfter={
				<NestedDropdownRightIcon label={formatMessage(aiMessages.changeToneOptionsIconLabel)} />
			}
			testId="dropdown-item__change-tone-menu"
		>
			{children}
		</ToolbarNestedDropdownMenu>
	);
};

const MakeLongerButton = () => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarDropdownItem
			elemBefore={<AILengthenIcon label="" />}
			testId="dropdown-item__make-longer"
		>
			{formatMessage(aiMessages.makeLongerToolbarDropdownIconTitle)}
		</ToolbarDropdownItem>
	);
};

const MoreProfessionalButton = () => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarDropdownItem
			elemBefore={<AIBriefcaseIcon label="" />}
			testId="dropdown-item__more-professional"
		>
			{formatMessage(aiMessages.moreProfessionalToneToolbarDropdownIconTitle)}
		</ToolbarDropdownItem>
	);
};

const MoreCasualButton = () => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarDropdownItem elemBefore={<AICasualIcon label="" />} testId="dropdown-item__more-casual">
			{formatMessage(aiMessages.moreCasualToneToolbarDropdownIconTitle)}
		</ToolbarDropdownItem>
	);
};

const MoreEmpatheticButton = () => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarDropdownItem
			elemBefore={<AIHeartIcon label="" />}
			testId="dropdown-item__more-empathetic"
		>
			{formatMessage(aiMessages.moreEmpatheticToneToolbarDropdownIconTitle)}
		</ToolbarDropdownItem>
	);
};

const SpellingAndGrammarButton = () => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarDropdownItem
			elemBefore={<AISpellcheckIcon label="" />}
			testId="dropdown-item__spelling-and-grammar"
		>
			{formatMessage(aiMessages.spellingAndGrammarToolbarDropdownIconTitle)}
		</ToolbarDropdownItem>
	);
};

const TranslateMenu = ({ children }: { children: React.ReactNode }) => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarNestedDropdownMenu
			elemBefore={<AITranslateIcon label="" />}
			text={formatMessage(aiMessages.translateToolbarDropdownIconTitle)}
			elemAfter={
				<NestedDropdownRightIcon label={formatMessage(aiMessages.translateOptionsIconLabel)} />
			}
			testId="dropdown-item__translate-menu"
		>
			{children}
		</ToolbarNestedDropdownMenu>
	);
};

const SummarizeButton = () => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarDropdownItem
			elemBefore={<AISummarizeIcon label="" />}
			testId="dropdown-item__summarize"
		>
			{formatMessage(aiMessages.summarizeToolbarDropdownIconTitle)}
		</ToolbarDropdownItem>
	);
};

const ImproveWritingButton = ({
	groupLocation,
}: {
	groupLocation?: ToolbarButtonGroupLocation;
}) => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarTooltip content={formatMessage(aiMessages.improveWritingToolbarIconTitle)}>
			<ToolbarButton
				iconBefore={<AICommandIcon label="" />}
				groupLocation={groupLocation}
				testId="editor-toolbar__improve-writing"
			>
				{formatMessage(aiMessages.improveWritingToolbarIconTitle)}
			</ToolbarButton>
		</ToolbarTooltip>
	);
};

export const AIToolbarComponents = (): RegisterComponent[] => {
	return [
		{
			type: AI_SECTION.type,
			key: AI_SECTION.key,
			parents: [
				{
					type: 'toolbar',
					key: TOOLBARS.INLINE_TEXT_TOOLBAR,
					rank: TOOLBAR_RANK[AI_SECTION.key],
				},
			],
		},
		{
			type: ROVO_GROUP.type,
			key: ROVO_GROUP.key,
			parents: [
				{
					type: AI_SECTION.type,
					key: AI_SECTION.key,
					rank: AI_SECTION_RANK[ROVO_GROUP.key],
				},
			],
		},
		{
			type: ROVO_HERO_BUTTON.type,
			key: ROVO_HERO_BUTTON.key,
			parents: [
				{
					type: ROVO_GROUP.type,
					key: ROVO_GROUP.key,
					rank: ROVO_GROUP_RANK[ROVO_HERO_BUTTON.key],
				},
			],
			component: RovoButton,
		},
		{
			type: ROVO_MENU.type,
			key: ROVO_MENU.key,
			parents: [
				{
					type: ROVO_GROUP.type,
					key: ROVO_GROUP.key,
					rank: ROVO_GROUP_RANK[ROVO_MENU.key],
				},
			],
			component: RovoMenu,
		},
		{
			type: ROVO_PROMPT_MENU_SECTION.type,
			key: ROVO_PROMPT_MENU_SECTION.key,
			parents: [
				{
					type: ROVO_MENU.type,
					key: ROVO_MENU.key,
					rank: ROVO_MENU_RANK[ROVO_PROMPT_MENU_SECTION.key],
				},
			],
		},
		{
			type: MAKE_SHORTER_MENU_ITEM.type,
			key: MAKE_SHORTER_MENU_ITEM.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[MAKE_SHORTER_MENU_ITEM.key],
				},
			],
			component: MakeShorterButton,
		},
		{
			type: MAKE_LONGER_MENU_ITEM.type,
			key: MAKE_LONGER_MENU_ITEM.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[MAKE_LONGER_MENU_ITEM.key],
				},
			],
			component: MakeLongerButton,
		},
		{
			type: CHANGE_TONE_NESTED_MENU.type,
			key: CHANGE_TONE_NESTED_MENU.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[CHANGE_TONE_NESTED_MENU.key],
				},
			],
			component: ChangeToneMenu,
		},
		{
			type: CHANGE_TONE_MENU_SECTION.type,
			key: CHANGE_TONE_MENU_SECTION.key,
			parents: [
				{
					type: CHANGE_TONE_NESTED_MENU.type,
					key: CHANGE_TONE_NESTED_MENU.key,
					rank: CHANGE_TONE_MENU_RANK[CHANGE_TONE_MENU_SECTION.key],
				},
			],
		},
		{
			type: MORE_PROFESSIONAL_MENU_ITEM.type,
			key: MORE_PROFESSIONAL_MENU_ITEM.key,
			parents: [
				{
					type: CHANGE_TONE_MENU_SECTION.type,
					key: CHANGE_TONE_MENU_SECTION.key,
					rank: CHANGE_TONE_MENU_SECTION_RANK[MORE_PROFESSIONAL_MENU_ITEM.key],
				},
			],
			component: MoreProfessionalButton,
		},
		{
			type: MORE_CASUAL_MENU_ITEM.type,
			key: MORE_CASUAL_MENU_ITEM.key,
			parents: [
				{
					type: CHANGE_TONE_MENU_SECTION.type,
					key: CHANGE_TONE_MENU_SECTION.key,
					rank: CHANGE_TONE_MENU_SECTION_RANK[MORE_CASUAL_MENU_ITEM.key],
				},
			],
			component: MoreCasualButton,
		},
		{
			type: MORE_EMPATHETIC_MENU_ITEM.type,
			key: MORE_EMPATHETIC_MENU_ITEM.key,
			parents: [
				{
					type: CHANGE_TONE_MENU_SECTION.type,
					key: CHANGE_TONE_MENU_SECTION.key,
					rank: CHANGE_TONE_MENU_SECTION_RANK[MORE_EMPATHETIC_MENU_ITEM.key],
				},
			],
			component: MoreEmpatheticButton,
		},
		{
			type: SPELLING_AND_GRAMMAR_MENU_ITEM.type,
			key: SPELLING_AND_GRAMMAR_MENU_ITEM.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[SPELLING_AND_GRAMMAR_MENU_ITEM.key],
				},
			],
			component: SpellingAndGrammarButton,
		},
		{
			type: TRANSLATE_NESTED_MENU.type,
			key: TRANSLATE_NESTED_MENU.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[TRANSLATE_NESTED_MENU.key],
				},
			],
			component: TranslateMenu,
		},
		{
			type: TRANSLATE_MENU_SECTION.type,
			key: TRANSLATE_MENU_SECTION.key,
			parents: [
				{
					type: TRANSLATE_NESTED_MENU.type,
					key: TRANSLATE_NESTED_MENU.key,
					rank: TRANSLATE_MENU_RANK[TRANSLATE_MENU_SECTION.key],
				},
			],
		},
		// placeholder menu item for languages
		{
			type: 'menu-item',
			key: 'language-one-item',
			parents: [
				{
					type: TRANSLATE_MENU_SECTION.type,
					key: TRANSLATE_MENU_SECTION.key,
					rank: 100,
				},
			],
			component: () => {
				return (
					<ToolbarDropdownItem elemBefore={<AITranslateIcon label="Translate" />}>
						Language 1
					</ToolbarDropdownItem>
				);
			},
		},
		{
			type: SUMMARIZE_MENU_ITEM.type,
			key: SUMMARIZE_MENU_ITEM.key,
			parents: [
				{
					type: ROVO_PROMPT_MENU_SECTION.type,
					key: ROVO_PROMPT_MENU_SECTION.key,
					rank: ROVO_PROMPT_MENU_SECTION_RANK[SUMMARIZE_MENU_ITEM.key],
				},
			],
			component: SummarizeButton,
		},
		{
			type: IMPROVE_WRITING_GROUP.type,
			key: IMPROVE_WRITING_GROUP.key,
			parents: [
				{
					type: AI_SECTION.type,
					key: AI_SECTION.key,
					rank: AI_SECTION_RANK[IMPROVE_WRITING_GROUP.key],
				},
			],
		},
		{
			type: IMPROVE_WRITING_BUTTON.type,
			key: IMPROVE_WRITING_BUTTON.key,
			parents: [
				{
					type: IMPROVE_WRITING_GROUP.type,
					key: IMPROVE_WRITING_GROUP.key,
					rank: IMPROVE_WRITING_GROUP_RANK[IMPROVE_WRITING_BUTTON.key],
				},
			],
			component: ImproveWritingButton,
		},
	];
};
